import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '~/components/ui';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  Table,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table/Table';
import type { ExtractedItem, UnitProgress } from '~/api/documentExtraction';
import { sourceKeyOf, sourceLabelerOf } from './sourceLabels';
import { VerdictBadge } from './VerdictBadge';
import {
  CONSUMED_FIELDS,
  EMPTY_CELL,
  IMPORT_COLUMNS,
  fieldOf,
  importRowOf,
  isFlagged,
} from './importHandoff';

const PAGE_SIZE = 100;
const NO_ITEMS: ReadonlySet<string> = new Set();
const NO_UNITS: readonly UnitProgress[] = [];

export function ItemTable({
  items,
  supersededItems = NO_ITEMS,
  units = NO_UNITS,
}: {
  items: ExtractedItem[];
  supersededItems?: ReadonlySet<string>;
  units?: readonly UnitProgress[];
}) {
  const { t } = useTranslation('document-extraction');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [shown, setShown] = useState(PAGE_SIZE);

  const flaggedCount = useMemo(() => items.filter(isFlagged).length, [items]);

  const filter = (onlyFlagged: boolean) => {
    setFlaggedOnly(onlyFlagged);
    setShown(PAGE_SIZE);
  };

  const matching = useMemo(() => {
    const filtered = flaggedOnly ? items.filter(isFlagged) : items;
    return [...filtered].sort(
      (left, right) => Number(isFlagged(right)) - Number(isFlagged(left))
    );
  }, [items, flaggedOnly]);

  const visible = useMemo(() => matching.slice(0, shown), [matching, shown]);

  // Both derive from every matching item, not the visible page: paging must extend the table,
  // not reshape it, and a group header counts the group rather than its first page.
  const evidenceColumns = useMemo(() => {
    const ordered: string[] = [];
    const seen = new Set<string>();
    matching.forEach((item) =>
      item.fields.forEach((field) => {
        if (!seen.has(field.name) && !CONSUMED_FIELDS.has(field.name)) {
          seen.add(field.name);
          ordered.push(field.name);
        }
      })
    );
    return ordered;
  }, [matching]);

  const byUnit = useMemo(() => {
    const labelOf = sourceLabelerOf(units);
    const groups = new Map<string, { label: string; items: ExtractedItem[] }>();
    matching.forEach((item) => {
      const key = sourceKeyOf(item.documentId, item.unitLabel);
      const group = groups.get(key);
      if (group) {
        group.items.push(item);
      } else {
        groups.set(key, {
          label: labelOf(item.documentId, item.unitLabel),
          items: [item],
        });
      }
    });
    return [...groups.entries()];
  }, [matching, units]);

  const columns: TableColumn[] = [
    ...IMPORT_COLUMNS.map((column) => ({
      key: column,
      header: t(`import_column.${column}`),
    })),
    ...evidenceColumns.map((column) => ({
      key: column,
      header: t(`field.${column}`),
      textShade: 'light' as const,
    })),
    { key: 'provenance', header: t('items.provenance') },
    { key: 'verdict', header: t('items.verdict') },
    { key: 'ambiguities', header: t('items.ambiguities') },
  ];

  const visibleIds = new Set(visible.map((item) => item.id));

  const rows: TableRow[] = byUnit.flatMap(([groupKey, group]) => {
    const unitItems = group.items;
    const onThisPage = unitItems.filter((item) => visibleIds.has(item.id));
    if (onThisPage.length === 0) {
      return [];
    }
    return [
      {
        id: { value: `unit:${groupKey}` },
        _sectionHeader: (
          <span data-testid="unit-group">
            {group.label}
            <span className="ml-2 font-normal text-neut-sec">
              ({unitItems.length})
            </span>
          </span>
        ),
      },
      ...onThisPage.map((item) =>
        rowOf(
          item,
          evidenceColumns,
          supersededItems.has(item.id),
          t('items.superseded')
        )
      ),
    ];
  });

  return (
    <SectionPanel
      title={
        <>
          {t('items.heading')}
          <span className="ml-2 font-normal">{items.length}</span>
        </>
      }
      aside={
        <div className="flex items-center gap-2">
          <Button
            variant={flaggedOnly ? 'ghost' : 'secondary'}
            size="sm"
            width="auto"
            onClick={() => filter(false)}
          >
            {t('items.filter_all')}
          </Button>
          <Button
            variant={flaggedOnly ? 'secondary' : 'ghost'}
            size="sm"
            width="auto"
            onClick={() => filter(true)}
          >
            {t('items.filter_flagged')} ({flaggedCount})
          </Button>
        </div>
      }
      bodyClassName="flex flex-col gap-4 p-4"
    >
      <Table
        columns={columns}
        rows={rows}
        emptyMessage={t('items.empty')}
        className="rounded-lg border border-neut-border"
      />

      {visible.length < matching.length && (
        <Button
          variant="secondary"
          size="sm"
          width="auto"
          onClick={() => setShown((count) => count + PAGE_SIZE)}
        >
          {t('items.show_more', {
            shown: visible.length,
            total: matching.length,
          })}
        </Button>
      )}
    </SectionPanel>
  );
}

function rowOf(
  item: ExtractedItem,
  evidenceColumns: string[],
  superseded: boolean,
  supersededLabel: string
): TableRow {
  const importRow = importRowOf(item);
  return {
    id: { value: item.id },
    _rowAttributes: {
      'data-testid': 'item-row',
      'data-verdict': item.verdict,
      ...(superseded
        ? { 'data-superseded': 'true', title: supersededLabel }
        : {}),
    },
    ...Object.fromEntries(
      IMPORT_COLUMNS.map((column) => [
        column,
        { value: importRow[column] || EMPTY_CELL },
      ])
    ),
    ...Object.fromEntries(
      evidenceColumns.map((column) => [
        column,
        { value: fieldOf(item, column) },
      ])
    ),
    provenance: {
      value: (
        <span data-testid="provenance" className="font-mono text-xs">
          {item.provenance}
        </span>
      ),
    },
    verdict: {
      value: (
        <VerdictBadge
          verdict={item.verdict}
          claimedValue={item.claimedValue}
          verifiedValue={item.verifiedValue}
          governingLabels={item.governingLabels}
          secondOpinion={item.secondOpinion}
          secondOpinionReason={item.secondOpinionReason}
        />
      ),
    },
    ambiguities: {
      value: item.ambiguities.map((note) => (
        <span key={note} className="block text-xs text-warning-800">
          {note}
        </span>
      )),
    },
  };
}
